import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";
import { usePublicData } from "@/hooks/usePublicData";
import { DEFAULT_PHONE, DEFAULT_EMAIL, SITE_ORIGIN } from "@/lib/publicSite";

export default function TermsConditions() {
  const { bizInfo } = usePublicData();
  const phone = bizInfo?.business_phone || DEFAULT_PHONE;
  const email = bizInfo?.business_email || DEFAULT_EMAIL;

  useSeoHeadTags({
    title: "Terms & Conditions | Sustainable Handyman Services",
    description: "Terms and Conditions for Sustainable Handyman Services - Our service terms, payment terms, and liability information.",
    canonical: `${SITE_ORIGIN}/terms`,
    ogType: "website",
  });

  return (
    <>
      
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <header className="bg-[#1e3a5f] text-white py-4">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80">
              <ArrowLeft className="h-5 w-5" />
              Back to Home
            </Link>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <span className="font-semibold">Terms &amp; Conditions</span>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-12">
          <div className="prose prose-slate max-w-none">
            <h1 className="text-3xl font-bold text-[#1e3a5f] mb-2">Terms &amp; Conditions</h1>
            <p className="text-muted-foreground mb-8">Last updated: February 2026</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">1. Introduction</h2>
              <p>
                These Terms and Conditions govern your use of services provided by Sustainable 
                Handyman Services. By booking our services, you agree to these Terms.
              </p>
              <p className="mt-4">
                <strong>Business Details:</strong><br />
                Sustainable Handyman Services<br />
                Abergavenny, Monmouthshire, Wales<br />
                Email: {email}<br />
                Phone: {phone}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">2. Services</h2>
              <p>We provide professional handyman, carpentry, and repair services including:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>General repairs and maintenance</li>
                <li>Carpentry and joinery work</li>
                <li>Kitchen and bathroom fitting</li>
                <li>Flooring installation</li>
                <li>Painting and decorating</li>
                <li>Garden structures and fencing</li>
                <li>Property maintenance</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">3. Quotations &amp; Pricing</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>All quotations are valid for 30 days unless otherwise stated</li>
                <li>Quotations are based on information provided and a site inspection where applicable</li>
                <li>Additional work discovered during the job may result in revised pricing</li>
                <li>VAT is not applicable (we are not VAT registered)</li>
                <li>Material costs are quoted separately and may vary based on supplier prices</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">4. Booking &amp; Cancellation</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Bookings are confirmed upon acceptance of a quotation</li>
                <li>Please provide at least 24 hours notice for cancellations</li>
                <li>Cancellations with less than 24 hours notice may incur a call-out charge</li>
                <li>We reserve the right to reschedule appointments due to unforeseen circumstances</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">5. Payment Terms</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Payment is due within 7 days of invoice date unless otherwise agreed</li>
                <li>For larger projects, we may require a deposit of up to 50%</li>
                <li>We accept bank transfer, cash, and card payments</li>
                <li>Late payments may incur interest at 8% above the Bank of England base rate</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">6. Guarantee &amp; Warranty</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Our workmanship is guaranteed for 12 months from completion</li>
                <li>This guarantee covers defects due to workmanship only</li>
                <li>Materials are covered by the manufacturer&#39;s warranty where applicable</li>
                <li>Normal wear and tear is not covered</li>
                <li>The guarantee does not cover damage caused by third parties or misuse</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">7. Liability</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>We carry public liability insurance of £2 million</li>
                <li>We will take reasonable care to prevent damage to your property</li>
                <li>Any damage caused by our work will be rectified at our expense</li>
                <li>We are not liable for pre-existing defects or issues discovered during work</li>
                <li>Our liability is limited to the value of the work undertaken</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">8. Access &amp; Site Conditions</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>You must ensure safe and clear access to the work area</li>
                <li>Please inform us of any hazards or special requirements</li>
                <li>Adequate parking should be available where possible</li>
                <li>We may need access to utilities (water, electricity) for certain jobs</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">9. Consumer Rights</h2>
              <p>
                Under the Consumer Rights Act 2015, you have certain statutory rights including:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Services must be performed with reasonable care and skill</li>
                <li>Materials supplied must be of satisfactory quality</li>
                <li>Work must be completed within a reasonable time if no time is agreed</li>
                <li>You have remedies if services are not provided as described</li>
              </ul>
              <p className="mt-4">
                These terms do not affect your statutory rights as a consumer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">10. Complaints</h2>
              <p>
                If you are unhappy with any aspect of our service, please contact us immediately at 
                Sandy@sustainablehandymanservices.uk. We aim to resolve all complaints within 14 days.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">11. Governing Law</h2>
              <p>
                These Terms are governed by the laws of England and Wales. Any disputes will be subject 
                to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">12. Changes to Terms</h2>
              <p>
                We may update these Terms from time to time. The current version will always be available 
                on our website.
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t">
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </main>
      </div>
    </>
  );
}
